import { Controller, useForm } from 'react-hook-form';
import { useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Pencil, User } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type CaregiverPersonalInfoFormData,
  caregiverPersonalInfoSchema,
  supervisorPersonalInfoSchema,
  type SupervisorPersonalInfoFormData,
} from '@/lib/validation';
import { formatPhoneNumber, stripPhoneFormatting } from '@/lib/phone-format';
import { getBrowserTimeZone, getSupportedTimeZones } from '@/lib/time-zones';
import type { PersonalInfoData } from '@/types/profile';
import { FormProgressBar } from '../common';
import type { NotificationType } from '@/types/profile';
import { OPTIN_COPY, NOTIFICATION_OPTIONS } from '@/lib/constants';

const PROFILE_PHOTO_ALLOWED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const PROFILE_PHOTO_MAX_SIZE_BYTES = 5 * 1024 * 1024;
const PROFILE_PHOTO_ACCEPT_ATTRIBUTE = PROFILE_PHOTO_ALLOWED_MIME_TYPES.join(',');

type PersonalInfoFormMode = 'caregiver' | 'supervisor';
type PersonalInfoFormData = SupervisorPersonalInfoFormData & {
  notificationPrefs?: CaregiverPersonalInfoFormData['notificationPrefs'];
};

interface PersonalInfoFormProps {
  onNext: (data: PersonalInfoData) => void;
  initialData?: PersonalInfoData;
  initialProfilePhotoFile?: File | null;
  onProfilePhotoChange?: (file: File | null) => void;
  isLoading?: boolean;
  mode?: PersonalInfoFormMode;
}

export function PersonalInfoForm({
  onNext,
  initialData,
  initialProfilePhotoFile = null,
  onProfilePhotoChange,
  isLoading = false,
  mode = 'caregiver',
}: PersonalInfoFormProps) {
  const isSupervisorMode = mode === 'supervisor';
  const [profilePhotoError, setProfilePhotoError] = useState<string | null>(null);
  const [selectedProfilePhotoFile, setSelectedProfilePhotoFile] = useState<File | null>(
    initialProfilePhotoFile
  );
  const [profilePhotoPreviewUrl, setProfilePhotoPreviewUrl] = useState<string | null>(null);
  const profilePhotoInputRef = useRef<HTMLInputElement>(null);
  const browserTimeZone = useMemo(() => getBrowserTimeZone(), []);
  const defaultTimeZone = initialData?.timeZone || browserTimeZone;
  const timeZoneOptions = useMemo(() => getSupportedTimeZones(defaultTimeZone), [defaultTimeZone]);

  useEffect(() => {
    if (!selectedProfilePhotoFile) {
      setProfilePhotoPreviewUrl(null);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedProfilePhotoFile);
    setProfilePhotoPreviewUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [selectedProfilePhotoFile]);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
    setValue,
    getValues,
    watch,
  } = useForm<PersonalInfoFormData>({
    resolver: zodResolver(
      isSupervisorMode ? supervisorPersonalInfoSchema : caregiverPersonalInfoSchema
    ),
    defaultValues: {
      firstName: initialData?.firstName || '',
      lastName: initialData?.lastName || '',
      phoneNumber: initialData?.phoneNumber || '',
      timeZone: defaultTimeZone,
      notificationPrefs: initialData?.notificationPrefs || [],
    },
    mode: 'onChange',
  });

  const formValues = watch();

  const handleNotificationTypeChange = (notificationType: NotificationType, checked: boolean) => {
    const currentTypes: NotificationType[] = getValues('notificationPrefs') || [];
    const newTypes = checked
      ? [...currentTypes, notificationType]
      : currentTypes.filter(type => type !== notificationType);
    setValue('notificationPrefs', newTypes, { shouldValidate: true });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue('phoneNumber', formatted, { shouldValidate: true });
  };

  const openProfilePhotoSelector = () => {
    setProfilePhotoError(null);
    profilePhotoInputRef.current?.click();
  };

  const handleProfilePhotoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!PROFILE_PHOTO_ALLOWED_MIME_TYPES.includes(file.type)) {
      setProfilePhotoError('Please upload a JPG, PNG, or WebP image.');
      e.target.value = '';
      return;
    }

    if (file.size > PROFILE_PHOTO_MAX_SIZE_BYTES) {
      setProfilePhotoError('Profile photo must be 5MB or smaller.');
      e.target.value = '';
      return;
    }

    setProfilePhotoError(null);
    setSelectedProfilePhotoFile(file);
    onProfilePhotoChange?.(file);
    e.target.value = '';
  };

  const onSubmit = (data: PersonalInfoFormData) => {
    // Strip phone formatting for API submission
    const submissionData: PersonalInfoData = {
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      phoneNumber: stripPhoneFormatting(data.phoneNumber),
      timeZone: data.timeZone.trim() || defaultTimeZone,
    };

    if (!isSupervisorMode) {
      submissionData.notificationPrefs = data.notificationPrefs;
    }

    onNext(submissionData);
  };

  // Calculate form completion
  const completedFields = [
    formValues.firstName,
    formValues.lastName,
    formValues.phoneNumber,
    ...(isSupervisorMode ? [] : [formValues.timeZone]),
  ].filter(
    value => value && typeof value === 'string' && value.trim().length > 0
  ).length;
  const totalFields = isSupervisorMode ? 3 : 4;
  const profilePhotoSrc = profilePhotoPreviewUrl;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Form Progress */}
      <FormProgressBar
        completedFields={completedFields}
        totalFields={totalFields}
        label={isSupervisorMode ? 'Supervisor information' : 'Personal information'}
      />

      {!isSupervisorMode && (
        <div className="rounded-xl border border-lilac-900 bg-white p-4">
          <Input
            ref={profilePhotoInputRef}
            data-testid="onboarding-profile-photo-input"
            aria-label="Upload profile photo"
            type="file"
            accept={PROFILE_PHOTO_ACCEPT_ATTRIBUTE}
            onChange={handleProfilePhotoFileChange}
            className="sr-only"
            disabled={isLoading}
          />
          <div className="flex flex-col items-center gap-3">
            <div className="relative h-28 w-28 shrink-0 aspect-square">
              <div className="h-full w-full rounded-full overflow-hidden bg-lilac-900 border-2 border-white shadow flex items-center justify-center">
                {profilePhotoSrc ? (
                  <img
                    src={profilePhotoSrc}
                    alt="Profile photo preview"
                    className="h-full w-full aspect-square rounded-full object-cover"
                  />
                ) : (
                  <User className="h-9 w-9 text-blueviolet" />
                )}
              </div>
              <Button
                type="button"
                size="icon"
                variant="outline"
                onClick={openProfilePhotoSelector}
                disabled={isLoading}
                aria-label={profilePhotoSrc ? 'Edit profile photo' : 'Add profile photo'}
                className="absolute -bottom-1 -right-1 z-10 h-8 w-8 rounded-full border border-night-100 bg-white shadow ring-2 ring-white"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-center text-sm text-night-500">
              Add a profile photo now, or add one later in Settings.
            </p>
            {profilePhotoError && (
              <p className="text-center text-sm text-error" role="alert">
                {profilePhotoError}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="firstName"
            className="block font-display text-sm font-semibold text-night-900 mb-2"
          >
            First Name <span className="text-tigerlily font-body" aria-label="required">*</span>
          </label>
          <Input
            id="firstName"
            type="text"
            {...register('firstName')}
            placeholder="Enter your first name"
            className={
              errors.firstName ? 'border-error focus-visible:border-error focus-visible:ring-error-dark' : ''
            }
            disabled={isLoading}
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-error" role="alert">
              {errors.firstName.message}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="lastName"
            className="block font-display text-sm font-semibold text-night-900 mb-2"
          >
            Last Name <span className="text-tigerlily font-body" aria-label="required">*</span>
          </label>
          <Input
            id="lastName"
            type="text"
            {...register('lastName')}
            placeholder="Enter your last name"
            className={
              errors.lastName ? 'border-error focus-visible:border-error focus-visible:ring-error-dark' : ''
            }
            disabled={isLoading}
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-error" role="alert">
              {errors.lastName.message}
            </p>
          )}
        </div>
      </div>

      <div>
        <label
          htmlFor="phoneNumber"
          className="block font-display text-sm font-semibold text-night-900 mb-2"
        >
          Phone Number <span className="text-tigerlily font-body" aria-label="required">*</span>
        </label>
        <Input
          id="phoneNumber"
          type="tel"
          value={formValues.phoneNumber}
          onChange={handlePhoneChange}
          placeholder="(555) 123-4567"
          className={
            errors.phoneNumber ? 'border-error focus-visible:border-error focus-visible:ring-error-dark' : ''
          }
          disabled={isLoading}
        />
        {errors.phoneNumber && (
          <p className="mt-1 text-sm text-error" role="alert">
            {errors.phoneNumber.message}
          </p>
        )}
        <p className="mt-1 text-sm text-night-500">
          {isSupervisorMode
            ? "We'll use this for account-related updates."
            : "We'll use this to send you booking notifications"}
        </p>
      </div>

      {!isSupervisorMode && (
        <div>
          <label
            htmlFor="timeZone"
            className="block font-display text-sm font-semibold text-night-900 mb-2"
          >
            Time Zone <span className="text-tigerlily font-body" aria-label="required">*</span>
          </label>
          <Controller
            name="timeZone"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange} disabled={isLoading}>
                <SelectTrigger
                  id="timeZone"
                  aria-label="Time Zone"
                  className={
                    errors.timeZone ? 'border-error focus-visible:border-error focus-visible:ring-error-dark' : ''
                  }
                >
                  <SelectValue placeholder="Select your time zone" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {timeZoneOptions.map(timeZone => (
                    <SelectItem key={timeZone} value={timeZone}>
                      {timeZone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.timeZone && (
            <p className="mt-1 text-sm text-error" role="alert">
              {errors.timeZone.message}
            </p>
          )}
          <p className="mt-1 text-sm text-night-500">Defaults to your browser time zone.</p>
        </div>
      )}

      {!isSupervisorMode && (
        <div className="rounded-xl border border-night-100 bg-lilac-100 p-4">
          <h3 className="font-body text-sm text-night-900 mb-4">{OPTIN_COPY}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {NOTIFICATION_OPTIONS.map(notification => (
              <div key={notification.value} className="flex items-center space-x-2">
                <Checkbox
                  id={notification.value}
                  checked={formValues.notificationPrefs?.includes(notification.value) || false}
                  onCheckedChange={checked =>
                    handleNotificationTypeChange(notification.value, checked as boolean)
                  }
                  disabled={isLoading}
                />
                <label
                  htmlFor={notification.value}
                  className="font-body text-sm text-night-900 cursor-pointer"
                >
                  {notification.label}
                </label>
              </div>
            ))}
          </div>
          {errors.notificationPrefs && (
            <p className="mt-2 text-sm text-error" role="alert">
              {errors.notificationPrefs.message}
            </p>
          )}
        </div>
      )}

      <div className="flex justify-end pt-4">
        <Button
          type="submit"
          disabled={!isValid || isLoading}
          loading={isLoading}
          loadingText="Saving..."
          className="px-8"
        >
          Continue
        </Button>
      </div>
    </form>
  );
}
